import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Plus, DoorOpen, Loader2, UserPlus } from "lucide-react";
import api from "../api";
import { Button, Input, Textarea, Select, Card, Badge } from "../components/ui";
import { useSuccessFailure } from "../context/SuccessFailureContext";

const WorkspacePage = () => {
  const { id } = useParams();
  const { showFailureModal, showSuccessModal } = useSuccessFailure();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [room, setRoom] = useState({ name: "", purpose: "" });
  const [member, setMember] = useState({ email: "", role: "member" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/communities/workspaces/${id}/`);
      setWs(res.data);
    } catch (e) {
      showFailureModal("Error", "Could not load this workspace.");
    } finally {
      setLoading(false);
    }
  }, [id, showFailureModal]);

  useEffect(() => { load(); }, [load]);

  const canManage = ws && (ws.my_role === "owner" || ws.my_role === "manager");

  const addRoom = async (e) => {
    e.preventDefault();
    if (!room.name.trim()) return;
    try { await api.post(`/communities/workspaces/${id}/rooms/`, room); setRoom({ name: "", purpose: "" }); load(); }
    catch (err) { showFailureModal("Failed", "Could not create room."); }
  };

  const addMember = async (e) => {
    e.preventDefault();
    if (!member.email.trim()) return;
    try {
      const res = await api.post(`/communities/workspaces/${id}/invite/`, member);
      setMember({ email: "", role: "member" });
      if (res.data?.status === "invited") {
        showSuccessModal("Invite sent", "They'll get an email to sign up and join automatically.");
      } else {
        showSuccessModal("Member added", "They can now access this workspace.");
      }
      load();
    } catch (err) {
      showFailureModal("Failed", err.response?.data?.detail || "Could not invite member.");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;
  if (!ws) return null;

  const m = ws.metrics || {};

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <Link to="/communities" className="text-sm text-indigo-600 dark:text-indigo-400">← All workspaces</Link>
        <div className="flex items-start justify-between mt-2 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{ws.name}</h1>
            {ws.description && <p className="text-gray-500 dark:text-gray-400">{ws.description}</p>}
          </div>
          <Badge tone="indigo">{ws.my_role}</Badge>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[["Rooms", m.rooms], ["Members", m.members], ["Tasks", m.tasks_total], ["Done", `${m.completion ?? 0}%`]].map(([label, val]) => (
            <Card key={label} className="p-4">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{val ?? 0}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Rooms */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Rooms</h2>
            {ws.rooms?.length === 0 && <p className="text-gray-500 dark:text-gray-400">No rooms yet.</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ws.rooms?.map((r) => (
                <Link key={r.id} to={`/communities/rooms/${r.id}`}>
                  <Card className="p-4 h-full hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-2">
                      <DoorOpen size={18} className="text-indigo-600 dark:text-indigo-300" />
                      <h3 className="font-semibold text-gray-900 dark:text-white">{r.name}</h3>
                    </div>
                    {r.purpose && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{r.purpose}</p>}
                    <p className="text-xs text-gray-400 mt-2">
                      {r.metrics?.done ?? 0}/{r.metrics?.tasks_total ?? 0} tasks done · {r.metrics?.completion ?? 0}%
                    </p>
                  </Card>
                </Link>
              ))}
            </div>
            {canManage && (
              <Card className="p-4">
                <form onSubmit={addRoom} className="space-y-2">
                  <p className="font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1"><Plus size={16} /> Add room</p>
                  <Input placeholder="Room name (e.g. Catering)" value={room.name}
                    onChange={(e) => setRoom({ ...room, name: e.target.value })} />
                  <Input placeholder="Purpose (optional)" value={room.purpose}
                    onChange={(e) => setRoom({ ...room, purpose: e.target.value })} />
                  <Button type="submit" size="sm">Create room</Button>
                </form>
              </Card>
            )}
          </div>

          {/* Members */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Members</h2>
            <Card className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-900 dark:text-white font-medium">{ws.owner_name}</span>
                <Badge tone="green">owner</Badge>
              </div>
              {ws.members?.map((mem) => (
                <div key={mem.id} className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300 text-sm">{mem.name}</span>
                  <Badge>{mem.role}</Badge>
                </div>
              ))}
            </Card>
            {canManage && (
              <Card className="p-4">
                <form onSubmit={addMember} className="space-y-2">
                  <p className="font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1"><UserPlus size={16} /> Invite member</p>
                  <Input placeholder="Member email" value={member.email}
                    onChange={(e) => setMember({ ...member, email: e.target.value })} />
                  <Select value={member.role} onChange={(e) => setMember({ ...member, role: e.target.value })}>
                    <option value="manager">Manager</option>
                    <option value="member">Member</option>
                    <option value="vendor">Vendor</option>
                    <option value="participant">Participant</option>
                  </Select>
                  <Button type="submit" size="sm">Add member</Button>
                </form>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspacePage;
